import React, { useContext, useEffect, useState } from 'react';
import { Button, Icon, Tooltip } from 'antd';
import { Bitrix24Consumer, Bitrix24Context } from 'contexts/legacy/Bitrix24Context';
import { LanguageConsumer } from 'contexts/legacy/LanguageContext';
import { SystemConsumer } from 'contexts/legacy/SystemContext';
import { api } from 'services/api';
import '../../styles/widgetTable.css';
import { FormattedMessage } from 'react-intl';
import moment from 'moment';
import { MonthPicker } from './MonthPicker';
import { WidgetData, WidgetDataResponse } from 'interfaces/widgetData';
import { WidgetDataChart } from './Charts/WidgetDataChart';

import WidgetDataTable from './WidgetDataTable';

function WidgetDataComponent() {
	const bitrix24 = useContext(Bitrix24Context);
	const [widgetData, setWidgetData] = useState<WidgetDataResponse>();
	const [dataSource, setDataSource] = useState<WidgetData[]>();
	const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
	const [selectedYear, setSelectedYear] = useState<number | null>(null);

	useEffect(() => {
		const auth = bitrix24.infos.auth;
		const domain = bitrix24.infos.domain;

		const fetchData = async () => {
			if (!auth || !domain) {
				return;
			}

			try {
				const { data } = await api.get(`insights/widget_data?auth=${auth}&domain=${domain}`);

				setWidgetData(data.data);
			} catch (error) {
				console.log('Erorr fetching data', error);
			}
		};

		fetchData();
	}, [bitrix24.infos.auth, bitrix24.infos.domain]);

	useEffect(() => {
		if (!widgetData) {
			return;
		}

		if (selectedMonth && selectedYear) {
			const widgetDataByDate = widgetData.byDate;
			setDataSource(widgetDataByDate[selectedYear][selectedMonth]);
			return;
		}

		setDataSource(widgetData.total);
	}, [selectedMonth, selectedYear, widgetData]);

	const jsonToCSV = (jsonData: WidgetData[]) => {
		try {
			const fields = ['number', 'redirects', 'clicks', 'url'];
			const replacer = (_key: string | number, value: string | number) =>
				value === null ? '' : value;

			const jsonToCSV = jsonData.map((row: WidgetData) => {
				return fields
					.map((fieldName) => {
						return JSON.stringify(row[fieldName as keyof WidgetData], replacer);
					})
					.join(',');
			});

			jsonToCSV.unshift(fields.join(','));
			const csv = jsonToCSV.join('\r\n');
			return csv;
		} catch (error) {
			console.error('Error converting JSON to CSV:', error);
			return null;
		}
	};

	const donwloadData = () => {
		if (!dataSource) return;

		const csvData = jsonToCSV(dataSource);
		if (csvData) {
			const blob = new Blob([csvData], { type: 'text/csv' });
			const url = URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.download =
				selectedMonth && selectedYear
					? `widget_data_${selectedMonth}/${selectedYear}.csv`
					: 'widget_data.csv';
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			URL.revokeObjectURL(url);
		}
	};

	const getDisabledDates = (pickableDate: moment.Moment | null) => {
		if (!widgetData) {
			return true;
		}

		if (!pickableDate) {
			return true;
		}

		const widgetDataByDate = widgetData.byDate;

		const messageDataYears = Object.keys(widgetDataByDate);
		const pickableDateYear = String(pickableDate.year());
		const pickableDateMonth = String(pickableDate.month() + 1);

		if (!messageDataYears.includes(pickableDateYear)) {
			return true;
		}

		const messageDataMonths = Object.keys(widgetDataByDate[pickableDateYear]);

		if (!messageDataMonths.includes(pickableDateMonth)) {
			return true;
		}

		return false;
	};

	return (
		<div>
			<WidgetDataChart widgetData={widgetData?.byDate} />

			<div className="tableWrapper">
				<div className="tableHeader">
					<Tooltip
						placement="leftBottom"
						title={<FormattedMessage id="widget_table_title_tooltip" />}
						arrowPointAtCenter
					>
						<h2 className="widgetTableTitle">
							<FormattedMessage id="widget_table_title" />
						</h2>
					</Tooltip>
					<Tooltip
						placement="leftBottom"
						title={<FormattedMessage id="widget_table_download" />}
						arrowPointAtCenter
					>
						{/* 
        // @ts-ignore */}
						<Icon
							className="widgetDataDownloadIcon"
							type="vertical-align-bottom"
							onClick={donwloadData}
						/>
					</Tooltip>
				</div>
				<div>
					<div className="createWidgetButtonWrapper">
						<MonthPicker
							getDisabledDates={getDisabledDates}
							setSelectedMonth={setSelectedMonth}
							setSelectedYear={setSelectedYear}
						/>
						<Tooltip title={<FormattedMessage id="create_widget_button_tooltip" />}>
							<a href={process.env.REACT_APP_WIDGET_BUILDER_URL} target="_blank" rel="noreferrer">
								<Button type="primary" size="large">
									<FormattedMessage id="create_widget_button" />
								</Button>
							</a>
						</Tooltip>
					</div>
					<WidgetDataTable dataSource={dataSource} />
				</div>
			</div>
		</div>
	);
}

export default LanguageConsumer(Bitrix24Consumer(SystemConsumer(WidgetDataComponent)));
